/**
 * Sector operations — Assets & Work Orders Service (R39 A8, SO-P67).
 * Manages asset records, history subscription, query constraints, and callable wrappers.
 */

import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { firestoreDb, firebaseStorage, firebaseAuth } from './firebase'
import { callable } from './callable'
import type {
  OpsAsset,
  OpsAssetHistoryEntry,
  CreateAssetInput,
  UpdateAssetInput,
  SetAssetStatusInput,
  IsolateAssetInput,
  ReleaseAssetInput,
  RetireAssetInput,
  RecordAssetServiceVisitInput,
  OpsAssetServiceVisit,
  OpsAssetServiceVisitKind,
  OpsAssetStatus,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function createAsset(
  input: CreateAssetInput,
): Promise<{ assetId: string; assetCode: string; status: OpsAssetStatus; version: number }> {
  return callable<CreateAssetInput, { assetId: string; assetCode: string; status: OpsAssetStatus; version: number }>(
    'createAsset',
    input,
  )
}

export async function updateAsset(
  input: UpdateAssetInput,
): Promise<{ assetId: string; version: number }> {
  return callable<UpdateAssetInput, { assetId: string; version: number }>(
    'updateAsset',
    input,
  )
}

export async function setAssetStatus(
  input: SetAssetStatusInput,
): Promise<{ assetId: string; status: OpsAssetStatus; version: number }> {
  return callable<SetAssetStatusInput, { assetId: string; status: OpsAssetStatus; version: number }>(
    'setAssetStatus',
    input,
  )
}

export async function isolateAsset(
  input: IsolateAssetInput,
): Promise<{ assetId: string; status: OpsAssetStatus; version: number }> {
  return callable<IsolateAssetInput, { assetId: string; status: OpsAssetStatus; version: number }>(
    'isolateAsset',
    input,
  )
}

export async function releaseAsset(
  input: ReleaseAssetInput,
): Promise<{ assetId: string; status: OpsAssetStatus; version: number }> {
  return callable<ReleaseAssetInput, { assetId: string; status: OpsAssetStatus; version: number }>(
    'releaseAsset',
    input,
  )
}

export async function retireAsset(
  input: RetireAssetInput,
): Promise<{ assetId: string; status: OpsAssetStatus; version: number }> {
  return callable<RetireAssetInput, { assetId: string; status: OpsAssetStatus; version: number }>(
    'retireAsset',
    input,
  )
}

export async function recordAssetServiceVisit(
  input: RecordAssetServiceVisitInput,
): Promise<{
  visitId: string
  assetId: string
  kind: OpsAssetServiceVisitKind
  visitDayKey: string
}> {
  return callable<
    RecordAssetServiceVisitInput,
    { visitId: string; assetId: string; kind: OpsAssetServiceVisitKind; visitDayKey: string }
  >('recordAssetServiceVisit', input)
}

export function subscribeAssetServiceVisits(
  _workspaceId: string,
  assetId: string,
  callback: (visits: OpsAssetServiceVisit[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const coll = collection(firestoreDb, 'ops_assets', assetId, 'service')
  const q = query(coll, orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsAssetServiceVisit, 'id'>),
      }))
      callback(items)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export const ALLOWED_ASSET_SERVICE_FILE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const

export const MAX_ASSET_SERVICE_FILE_BYTES = 10 * 1024 * 1024 // 10 MB

export interface UploadAssetServiceFileResult {
  path: string
  downloadUrl: string
}

export async function uploadAssetServiceFile(
  workspaceId: string,
  assetId: string,
  file: File,
): Promise<UploadAssetServiceFileResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!assetId) throw new Error('assetId is required.')

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for service file upload.')
  }

  if (file.size > MAX_ASSET_SERVICE_FILE_BYTES) {
    throw new Error('File size exceeds 10 MB limit.')
  }

  const isAllowedType =
    file.type.startsWith('image/') ||
    file.type === 'application/pdf' ||
    ALLOWED_ASSET_SERVICE_FILE_MIME_TYPES.includes(
      file.type as (typeof ALLOWED_ASSET_SERVICE_FILE_MIME_TYPES)[number],
    )
  if (!isAllowedType) {
    throw new Error('Invalid file type. Only images and PDF files are allowed.')
  }

  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const path = `workspaces/${workspaceId}/ops_assets/${assetId}/${uid}/${Date.now()}_${safeFileName}`
  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, { contentType: file.type })
  const downloadUrl = await getDownloadURL(storageRef)
  return { path, downloadUrl }
}

export async function getAssetServiceFileUrl(path: string): Promise<string> {
  if (!path) return ''
  const storageRef = ref(firebaseStorage, path)
  return getDownloadURL(storageRef)
}

// ---------------------------------------------------------------------------
// Query Constraints & Subscriptions
// ---------------------------------------------------------------------------

export interface OpsAssetQueryOptions {
  callerUid?: string
  viewerUid?: string
  isElevated?: boolean
  isRestricted?: boolean
  includeRetired?: boolean
}

/**
 * Pure builder for Firestore query constraints used by subscribeOpsAssets and rules tests.
 * Rules require non-restricted members or (isRestricted & (readAccess == null | contains caller)).
 */
export function buildOpsAssetQueryConstraints(
  workspaceId: string,
  options: OpsAssetQueryOptions,
): QueryConstraint[][] {
  const callerUid = options.callerUid?.trim() || options.viewerUid?.trim()
  if (!callerUid) {
    return []
  }

  const queries: QueryConstraint[][] = []
  const retired = options.includeRetired === true

  if (options.isRestricted === true) {
    // 1. Assets with no place restriction (readAccess == null)
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
      where('retired', '==', retired),
      orderBy('assetCode', 'asc'),
      limit(200),
    ])

    // 2. Assets where place restriction includes caller
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
      where('retired', '==', retired),
      orderBy('assetCode', 'asc'),
      limit(200),
    ])
  } else {
    // Plain or elevated member: can read all assets in workspace
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('retired', '==', retired),
      orderBy('assetCode', 'asc'),
      limit(200),
    ])
  }

  return queries
}

export function subscribeOpsAssets(
  workspaceId: string,
  options: OpsAssetQueryOptions,
  onUpdate: (items: OpsAsset[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const callerUid = options.callerUid?.trim() || options.viewerUid?.trim()
  if (!callerUid) {
    onUpdate([])
    return () => {}
  }

  const queryGroups = buildOpsAssetQueryConstraints(workspaceId, options)
  if (queryGroups.length === 0) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_assets')
  const streamDocs: OpsAsset[][] = []
  const unsubs: Unsubscribe[] = []

  const mergeAndEmit = () => {
    const map = new Map<string, OpsAsset>()
    for (const group of streamDocs) {
      for (const item of group) {
        map.set(item.id, item)
      }
    }
    const merged = Array.from(map.values())
    merged.sort(
      (a, b) =>
        a.assetCode.localeCompare(b.assetCode) ||
        a.name.localeCompare(b.name) ||
        a.id.localeCompare(b.id),
    )
    onUpdate(merged)
  }

  queryGroups.forEach((constraints, index) => {
    streamDocs[index] = []
    const q = query(coll, ...constraints)
    const unsub = onSnapshot(
      q,
      (snap) => {
        streamDocs[index] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<OpsAsset, 'id'>),
        }))
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )
    unsubs.push(unsub)
  })

  return () => {
    for (const u of unsubs) {
      u()
    }
  }
}

export function subscribeOpsAssetHistory(
  assetId: string,
  onUpdate: (history: OpsAssetHistoryEntry[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const coll = collection(firestoreDb, `ops_assets/${assetId}/history`)
  const q = query(coll, orderBy('timestamp', 'desc'), limit(100))

  return onSnapshot(
    q,
    (snap) => {
      const history = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsAssetHistoryEntry, 'id'>),
      }))
      onUpdate(history)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}
